/**
 * Clerk session-cookie helpers, split out of `rpc-clients/shared.ts`.
 *
 * These two functions read and decode the `__session` cookie synchronously and
 * depend on nothing but `effect` core (`Either`). They are extracted here so
 * that lightweight consumers — notably `atoms/clerk-auth.ts`, which is reached
 * from the always-loaded document root via `PostHogSync` — can read the session
 * token WITHOUT importing `shared.ts`, whose `@effect/rpc` + `@effect/platform`
 * RPC plumbing would otherwise be dragged into the `main` chunk and load on
 * every marketing route (CLA-645). `shared.ts` re-exports both so its existing
 * importers are unaffected.
 *
 * @since 1.0.0
 */
import { Either } from "effect";

/**
 * Decode a JWT's `exp` claim to epoch milliseconds, or `null` when the token
 * is not parseable as a JWT or carries no numeric `exp`.
 *
 * Exported for unit tests.
 *
 * @since 1.0.0
 */
export const jwtExpiryMs = (token: string): number | null => {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  return Either.match(
    Either.try((): unknown => JSON.parse(atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/")))),
    {
      onLeft: () => null,
      onRight: (payload) => {
        const exp = (payload as { exp?: unknown }).exp;
        return typeof exp === "number" ? exp * 1000 : null;
      },
    },
  );
};

/**
 * Synchronously read the Clerk session cookie from `document.cookie`.
 *
 * The `__session` cookie's value IS the JWT — the same token the API
 * verifies networklessly via `verifyToken({ jwtKey })` in
 * `packages/api/src/rpc/auth.middleware.ts:117`. Clerk sets the cookie
 * without `HttpOnly` precisely so their browser SDK can read it; we can
 * do the same and skip the SDK round-trip entirely. This eliminates the
 * `globalThis.__clerkSession` race where polling atoms fire before
 * `ClerkAuthSync` has populated the global ref (manifests as 0 ms
 * `Unauthorized` RPC failures on first paint and during session refresh).
 *
 * A cookie whose JWT is provably expired is treated as absent. Clerk JWTs
 * live ~60 s, so on a page load after the tab was closed or idle the cookie
 * is routinely stale until clerk-js boots and rotates it. Trusting it made
 * `authGateAtom` report `"authed"`, data atoms fired once with the rotten
 * token, the API rejected them with `Unauthorized: Invalid session token`,
 * and — because the gate's value never changed when clerk-js then finished
 * booting — the atoms never refired and pages stuck on the error. Returning
 * `null` instead resolves the gate to `"undetermined"`, so atoms wait for
 * clerk-js and fire once with a fresh SDK token. Tokens without a readable
 * `exp` are returned as-is (the API is the authority on validity).
 *
 * Returns `null` when called server-side (no `document`), when no session
 * cookie is present (user not signed in), or when the cookie JWT is expired.
 *
 * @since 1.0.0
 */
export const readClerkSessionCookieToken = (): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)__session=([^;]+)/);
  if (!match) return null;
  const token = decodeURIComponent(match[1]!);
  const expMs = jwtExpiryMs(token);
  return expMs !== null && expMs <= Date.now() ? null : token;
};

/**
 * Whether a `__session` cookie is present at all, regardless of whether its
 * JWT has expired.
 *
 * {@link readClerkSessionCookieToken} collapses "no cookie" and "expired
 * cookie" into a single `null`, but the two demand opposite handling by the
 * token getter: an EXPIRED cookie is evidence the user WAS signed in and
 * clerk-js will shortly rotate a fresh JWT (so it is worth waiting for
 * hydration), whereas a WHOLLY ABSENT cookie means a signed-out / public
 * caller for whom waiting is pointless and would only add latency. This
 * predicate lets the getter tell them apart.
 *
 * Returns `false` server-side (no `document`).
 *
 * @since 1.0.0
 */
export const hasClerkSessionCookie = (): boolean => {
  if (typeof document === "undefined") return false;
  return /(?:^|;\s*)__session=/.test(document.cookie);
};
